import { API_BASE_URL } from '@synergy/config';
import { DownloadIcon } from '@synergy/theme-next';
import { Filter, PoolChip, SearchFilter } from '@synergy/components';
import { FormattedMessage } from 'react-intl';
import { PaloActivityDock, PaloOverflowMenu } from '@synergy/components-palo';
import { ReactElement, useEffect } from 'react';
import { Stack } from '@mui/material';
import { getBonusSettlementTranslationId } from '@synergy/models';
import { useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import urlJoin from 'url-join';

import { BonusSettlementDetailSidebar } from './components/bonus-settlement-detail-sidebar';
import { BonusSettlementDetailTable } from './components/bonus-settlement-customer-detail-table';
import { PaloOverview } from '../../../common/components/palo/palo-overview';
import { ROUTES } from '../../sidebar-menu/navigation-drawer/routes';
import {
    bonusSettlementCustomerDetailSelector,
    bonusSettlementCustomerDetailSidebarSelector,
    bonusSettlementsCustomerDetailRequestingSelector,
} from './store/bonus-settlements-customer-detail-selectors';
import {
    getBonusSettlementCustomerDetailSidebar,
    getBonusSettlementCustomerDetails,
    resetBonusSettlementsCustomerDetailState,
} from './store/bonus-settlements-customer-detail-actions';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

export const BonusSettlementCustomerDetail = (): ReactElement | null => {
    const dispatch = useThunkDispatch();
    const currency = useAppSelector(state => state.settings.currency);
    const navigate = useNavigate();
    const requesting = useAppSelector(bonusSettlementsCustomerDetailRequestingSelector);

    const { bonusSettlementCustomers } = useAppSelector(bonusSettlementCustomerDetailSelector);
    const { details } = useAppSelector(bonusSettlementCustomerDetailSidebarSelector);

    const { bonusSettlementId, id: customerId } = useParams<{ bonusSettlementId: string; id: string }>();
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        if (bonusSettlementId && customerId) {
            dispatch(
                getBonusSettlementCustomerDetails({
                    id: +bonusSettlementId,
                    customerId: +customerId,
                    searchParams,
                })
            );
            dispatch(
                getBonusSettlementCustomerDetailSidebar({
                    bonusSettlementId: +bonusSettlementId,
                    customerId: +customerId,
                })
            );
        }
        return () => {
            dispatch(resetBonusSettlementsCustomerDetailState());
        };
    }, [bonusSettlementId, customerId, searchParams, dispatch]);

    if (!bonusSettlementCustomers) {
        return null;
    }

    const handleExport = () => {
        window.open(
            urlJoin(API_BASE_URL, `/bonus-settlements/${bonusSettlementId}/customers/${customerId}/export-pdf`),
            '_self'
        );
    };

    const handleReset = () => {
        dispatch(resetBonusSettlementsCustomerDetailState());
    };

    return (
        <PaloOverview
            dock={
                <PaloActivityDock
                    defaultOpen={false}
                    panels={[
                        {
                            key: 'details',
                            label: <FormattedMessage id="app.common.details" />,
                            content: <BonusSettlementDetailSidebar currency={currency} bonusSettlement={details} />,
                        },
                    ]}
                />
            }
            plain={true}
            titleMessage={details ? `${details?.customerName} (${details?.year})` : <span>&ndash;</span>}
            requesting={requesting}
            onResetData={handleReset}
            filters={
                <Filter searchParams={searchParams} onUpdateSearchParams={setSearchParams}>
                    <SearchFilter />
                </Filter>
            }
            breadcrumbs={[
                {
                    label: <FormattedMessage id="app.navigation.bonusSettlements" />,
                    onClick: () => navigate(ROUTES.bonusSettlements.overview),
                },
                {
                    label: <FormattedMessage id="app.bonusSettlements.tab.customer" />,
                    onClick: () =>
                        navigate(ROUTES.bonusSettlements.detail.customer.replace(':id', String(bonusSettlementId))),
                },
            ]}
            additionalItemsRight={[
                // The pill says what the status is; the word "Status" in white
                // in front of it comes from the old dark toolbar and is
                // invisible on this one.
                <PoolChip
                    key="status"
                    listItem={true}
                    status={details?.status}
                    label={<FormattedMessage id={getBonusSettlementTranslationId(details?.status, details?.type)} />}
                />,
                <PaloOverflowMenu
                    key="actions"
                    actions={[
                        {
                            label: <FormattedMessage id="app.bonusSettlements.customerDetail.exportMemberPdf" />,
                            onClick: handleExport,
                        },
                    ]}
                />,
            ]}
        >
            <BonusSettlementDetailTable
                currency={currency}
                requesting={requesting}
                items={bonusSettlementCustomers.items}
            />
        </PaloOverview>
    );
};
